import { normalizePayments } from '~shared/helpers/normalize-payments';
import { capitalizeWords } from '~shared/helpers/text-formatting';

export type PaymentMethodSummaryRow = {
  method?: string | null;
  amount?: number | string | null;
  isReversal?: boolean;
  reversedAt?: string | Date | null;
};

const PAYMENT_METHOD_DISPLAY_LABELS: Record<string, string> = {
  card: 'Card',
  cash: 'Cash',
  check: 'Check',
  wallet: 'Wallet',
  us_bank_account: 'ACH Transfer',
  au_becs_debit: 'BECS',
  link: 'Link',
};

const paymentMethodDisplayLabel = (method: string): string =>
  PAYMENT_METHOD_DISPLAY_LABELS[method] ?? capitalizeWords(method);

/** The reversal leg itself, or an original that has since been reversed. */
const isReversalLeg = (payment: PaymentMethodSummaryRow) =>
  Boolean(payment.isReversal) ||
  Boolean(payment.reversedAt) ||
  Number(payment.amount ?? 0) < 0;

const isOriginalLeg = (payment: PaymentMethodSummaryRow) =>
  !payment.isReversal && Number(payment.amount ?? 0) >= 0;

/**
 * One label per payment method on an invoice, deduped.
 *
 * Every record and every reversal appends to `payments[]`, so repeated cash
 * reversals would otherwise render as "cash, cash, cash, ...". A method that
 * was both paid and reversed is labelled "Cash/Reversed"; credit-memo rows,
 * which only carry the reversal leg, keep the plain method name.
 */
export const formatPaymentMethodSummary = (
  payments: Array<PaymentMethodSummaryRow> | null | undefined,
): string => {
  const methods = new Map<
    string,
    { label: string; hasOriginal: boolean; hasReversal: boolean }
  >();

  for (const payment of normalizePayments(payments)) {
    const method = payment.method?.trim().toLowerCase();
    if (!method) continue;

    const entry = methods.get(method) ?? {
      label: paymentMethodDisplayLabel(method),
      hasOriginal: false,
      hasReversal: false,
    };

    methods.set(method, {
      label: entry.label,
      hasOriginal: entry.hasOriginal || isOriginalLeg(payment),
      hasReversal: entry.hasReversal || isReversalLeg(payment),
    });
  }

  return [...methods.values()]
    .map((entry) =>
      entry.hasOriginal && entry.hasReversal
        ? `${entry.label}/Reversed`
        : entry.label,
    )
    .join(', ');
};
